import React, { useState, useEffect, ChangeEvent } from 'react';
import { Modal, Form, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CustomField } from '../../../../types/camposPersonalizados';
import CurrencyInput from 'react-currency-input-field';

// Interface para opções do Select
interface SelectOption {
    id: string | number;
    value: string | number;
    label: string;
}

// Tipos das props do componente
interface EditFieldModalProps {
    show: boolean;
    onHide: () => void;
    field: {
        id: number;
        titulo: string;
        tipo_campo: string;
        opcoes?: any[];
        valorAtual: any;
    } | null;
    onSave: (fieldId: number, value: any) => Promise<void>;
    isUpdating: boolean;
}

const EditFieldModal: React.FC<EditFieldModalProps> = ({
    show,
    onHide,
    field,
    onSave,
    isUpdating
}) => {
    const [value, setValue] = useState<any>(null);

    // Inicializar o valor ao abrir o modal
    useEffect(() => {
        if (field) {
            setValue(field.valorAtual);
        }
    }, [field]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!field) return;

        await onSave(field.id, value);
    };

    // Função para aplicar máscara de telefone
    const formatTelefone = (input: string): string => {
        // Remove todos os caracteres não numéricos
        const numeros = input.replace(/\D/g, '');

        // Aplica a máscara (99) 99999-9999
        if (numeros.length <= 11) {
            let resultado = '';

            if (numeros.length > 0) {
                resultado += '(' + numeros.substring(0, 2);
            }
            if (numeros.length > 2) {
                resultado += ') ' + numeros.substring(2, 7);
            }
            if (numeros.length > 7) {
                resultado += '-' + numeros.substring(7, 11);
            }

            return resultado;
        }

        return input;
    };

    // Função para aplicar máscara de CPF
    const formatCPF = (input: string): string => {
        // Remove todos os caracteres não numéricos
        const numeros = input.replace(/\D/g, '');

        // Aplica a máscara 999.999.999-99
        if (numeros.length <= 11) {
            let resultado = '';

            if (numeros.length > 0) {
                resultado += numeros.substring(0, 3);
            }
            if (numeros.length > 3) {
                resultado += '.' + numeros.substring(3, 6);
            }
            if (numeros.length > 6) {
                resultado += '.' + numeros.substring(6, 9);
            }
            if (numeros.length > 9) {
                resultado += '-' + numeros.substring(9, 11);
            }

            return resultado;
        }

        return input;
    };

    // Função para aplicar máscara de CNPJ
    const formatCNPJ = (input: string): string => {
        // Remove todos os caracteres não numéricos
        const numeros = input.replace(/\D/g, '');

        // Aplica a máscara 99.999.999/9999-99
        if (numeros.length <= 14) {
            let resultado = '';

            if (numeros.length > 0) {
                resultado += numeros.substring(0, 2);
            }
            if (numeros.length > 2) {
                resultado += '.' + numeros.substring(2, 5);
            }
            if (numeros.length > 5) {
                resultado += '.' + numeros.substring(5, 8);
            }
            if (numeros.length > 8) {
                resultado += '/' + numeros.substring(8, 12);
            }
            if (numeros.length > 12) {
                resultado += '-' + numeros.substring(12, 14);
            }

            return resultado;
        }

        return input;
    };

    // Handler para valores com máscara
    const handleMaskedInputChange = (e: React.ChangeEvent<any>, maskFunction: (input: string) => string) => {
        const input = e.target.value;
        const formattedValue = maskFunction(input);
        setValue(formattedValue);
    };

    if (!field) return null;

    // Estilos personalizados para os componentes Select e react-select
    // Estilos melhorados para garantir fundo branco em todos os elementos
    const selectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
            borderColor: state.isFocused ? '#80bdff' : '#ced4da',
            backgroundColor: 'white', // Garantir fundo branco
            '&:hover': {
                borderColor: '#80bdff'
            },
            padding: '4px 8px',
            minHeight: '48px',
            fontSize: '1rem'
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#007bff' 
                : state.isFocused 
                    ? '#f8f9fa' 
                    : 'white',
            color: state.isSelected ? 'white' : '#212529',
            padding: '10px 15px',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: state.isSelected ? '#007bff' : '#e9ecef'
            }
        }),
        multiValue: (base: any) => ({
            ...base,
            backgroundColor: '#e9ecef',
            borderRadius: '4px'
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            color: '#495057',
            padding: '4px 6px'
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            color: '#6c757d',
            '&:hover': {
                backgroundColor: '#dee2e6',
                color: '#343a40'
            }
        }),
        menu: (base: any) => ({
            ...base,
            zIndex: 9999,
            boxShadow: '0 .5rem 1rem rgba(0,0,0,.15)',
            borderRadius: '6px',
            marginTop: '4px',
            backgroundColor: 'white' // Forçar fundo branco para o menu
        }),
        menuList: (base: any) => ({
            ...base,
            backgroundColor: 'white' // Forçar fundo branco para a lista
        }),
        placeholder: (base: any) => ({
            ...base,
            color: '#adb5bd',
            fontSize: '1rem'
        }),
        input: (base: any) => ({
            ...base,
            color: '#212529',
            fontSize: '1rem'
        }),
        singleValue: (base: any) => ({
            ...base,
            color: '#212529',
            fontSize: '1rem'
        }),
        indicatorSeparator: () => ({
            display: 'none' // Remove a linha separadora
        }),
        dropdownIndicator: (base: any) => ({
            ...base,
            color: '#6c757d',
            '&:hover': {
                color: '#495057'
            }
        }),
        clearIndicator: (base: any) => ({
            ...base,
            color: '#6c757d',
            '&:hover': {
                color: '#495057'
            }
        }),
        noOptionsMessage: (base: any) => ({
            ...base,
            color: '#6c757d',
            padding: '10px 15px',
            backgroundColor: 'white' // Fundo branco para mensagem de sem opções
        }),
        valueContainer: (base: any) => ({
            ...base,
            backgroundColor: 'white', // Fundo branco para o container de valores
            padding: '2px 8px'
        })
    };

    // Preparar opções para select e multiselect
    const prepareOptions = (): SelectOption[] => {
        if (!field?.opcoes || !Array.isArray(field.opcoes) || field.opcoes.length === 0) {
            return [];
        }

        // Garantindo que cada opção tenha id, value e label
        return field.opcoes.map(opt => {
            // Normalizar objetos de opção para garantir que tenham o formato esperado
            if (typeof opt === 'object') {
                return {
                    id: opt.id || opt.value,
                    value: opt.value || opt.id,
                    label: opt.label || opt.nome || opt.value || opt.id
                };
            }
            // Se for um valor primitivo
            return {
                id: opt,
                value: opt,
                label: String(opt)
            };
        });
    };

    const renderFieldInput = () => {
        const options = prepareOptions();

        switch (field.tipo_campo) {
            case 'texto':
            case 'email':
                return (
                    <Form.Control
                        type={field.tipo_campo === 'email' ? 'email' : 'text'}
                        value={value || ''}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={`Digite ${field.titulo}`}
                        className="form-control-lg"
                        aria-label={field.titulo}
                    />
                );

            case 'textarea':
                return (
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={value || ''}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={`Digite ${field.titulo}`}
                        className="form-control-lg"
                        aria-label={field.titulo}
                    />
                );

            case 'telefone':
                return (
                    <Form.Control
                        type="text"
                        value={value || ''}
                        onChange={(e) => handleMaskedInputChange(e, formatTelefone)}
                        placeholder="(99) 99999-9999"
                        maxLength={15}
                        className="form-control-lg"
                        aria-label="Telefone"
                    />
                );

            case 'cpf':
                return (
                    <Form.Control
                        type="text"
                        value={value || ''}
                        onChange={(e) => handleMaskedInputChange(e, formatCPF)}
                        placeholder="000.000.000-00"
                        maxLength={14}
                        className="form-control-lg"
                        aria-label="CPF"
                    />
                );

            case 'cnpj':
                return (
                    <Form.Control
                        type="text"
                        value={value || ''}
                        onChange={(e) => handleMaskedInputChange(e, formatCNPJ)}
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        className="form-control-lg"
                        aria-label="CNPJ"
                    />
                );

            case 'numero':
                return (
                    <Form.Control
                        type="number"
                        value={value || ''}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="0"
                        className="form-control-lg"
                        aria-label={`Número - ${field.titulo}`}
                    />
                );

            case 'monetario':
                return (
                    <CurrencyInput
                        prefix="R$ "
                        decimalsLimit={2}
                        decimalSeparator=","
                        groupSeparator="."
                        value={value}
                        onValueChange={(value) => setValue(value)}
                        placeholder="R$ 0,00"
                        className="form-control form-control-lg"
                        aria-label={`Valor monetário - ${field.titulo}`}
                    />
                );

            case 'porcentagem':
                return (
                    <InputGroup className="mb-3">
                        <Form.Control
                            type="number"
                            value={value || ''}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.01"
                            className="form-control-lg"
                            aria-label={`Porcentagem - ${field.titulo}`}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                );

            case 'data':
                return (
                    <DatePicker
                        selected={value ? new Date(value) : null}
                        onChange={(date) => setValue(date ? date.toISOString().split('T')[0] : null)}
                        dateFormat="dd/MM/yyyy"
                        className="form-control form-control-lg w-100"
                        placeholderText="dd/mm/aaaa"
                        aria-label={`Data - ${field.titulo}`}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        isClearable
                    />
                );

            case 'hora':
                return (
                    <Form.Control
                        type="time"
                        value={value || ''}
                        onChange={(e) => setValue(e.target.value)}
                        className="form-control-lg"
                        aria-label={`Hora - ${field.titulo}`}
                    />
                );

            case 'checkbox':
                return (
                    <div className="form-check form-check-lg mt-2">
                        <Form.Check
                            type="checkbox"
                            id={`checkbox-${field.id}`}
                            checked={value === "1" || value === true}
                            onChange={(e) => setValue(e.target.checked ? "1" : "0")}
                            label={field.titulo}
                            className="fs-5"
                        />
                    </div>
                );

            case 'select':
                // Encontrar a opção selecionada com tratamento adequado para valores nulos/indefinidos
                const selectedOption = value ?
                    options.find(option => String(option.id) === String(value) || String(option.value) === String(value)) :
                    null;

                return (
                    <div className="select-wrapper">
                        <Select
                            options={options}
                            value={selectedOption}
                            onChange={(selected) => setValue(selected?.id || selected?.value || null)}
                            placeholder="Selecione uma opção"
                            className="basic-select"
                            classNamePrefix="select"
                            styles={selectStyles}
                            isClearable={true}
                            aria-label={`Selecione - ${field.titulo}`}
                            noOptionsMessage={() => "Nenhuma opção cadastrada"}
                            loadingMessage={() => "Carregando..."}
                        />
                    </div>
                );

            case 'multiselect':
                // Definição explícita do tipo para selectedValues
                let selectedValues: SelectOption[] = [];

                // Converter o valor atual para o formato esperado pelo Select
                try {
                    if (typeof value === 'string') {
                        if (value.startsWith('[')) {
                            // Valor é uma string JSON de array
                            const parsedValues = JSON.parse(value);
                            if (Array.isArray(parsedValues)) {
                                selectedValues = parsedValues.map((val: any) => {
                                    // Buscar a opção correspondente pelo id ou value
                                    const option = options.find(opt =>
                                        String(opt.id) === String(val) ||
                                        String(opt.value) === String(val)
                                    );
                                    return option || null;
                                }).filter(Boolean) as SelectOption[]; // Remover valores nulos
                            }
                        } else if (value) {
                            // Valor é uma string simples
                            const option = options.find(opt =>
                                String(opt.id) === String(value) ||
                                String(opt.value) === String(value)
                            );
                            if (option) selectedValues = [option];
                        }
                    } else if (Array.isArray(value)) {
                        // Valor já é um array
                        selectedValues = value.map((val: any) => {
                            // Buscar a opção correspondente
                            const option = options.find(opt =>
                                String(opt.id) === String(val) ||
                                String(opt.value) === String(val)
                            );
                            return option || null;
                        }).filter(Boolean) as SelectOption[]; // Remover valores nulos
                    }
                } catch (e) {
                    console.error('Erro ao processar valores multiselect:', e);
                    selectedValues = [];
                }

                return (
                    <div className="select-wrapper">
                        <Select
                            options={options}
                            isMulti={true}
                            value={selectedValues}
                            onChange={(selected) => {
                                if (Array.isArray(selected) && selected.length > 0) {
                                    const values = selected.map(item => item.id || item.value);
                                    setValue(values);
                                } else {
                                    setValue(null);
                                }
                            }}
                            placeholder="Selecione opções"
                            className="basic-multi-select"
                            classNamePrefix="select"
                            styles={selectStyles}
                            aria-label={`Selecione múltiplos - ${field.titulo}`}
                            noOptionsMessage={() => "Nenhuma opção cadastrada"}
                            loadingMessage={() => "Carregando..."}
                        />
                    </div>
                );

            default:
                return (
                    <Form.Control
                        type="text"
                        value={value || ''}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={`Digite ${field.titulo}`}
                        className="form-control-lg"
                        aria-label={field.titulo}
                    />
                );
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            keyboard={false}
            size="lg"
            aria-labelledby="field-edit-modal-title"
        >
            <Modal.Header closeButton className="bg-light">
                <Modal.Title id="field-edit-modal-title">
                    Editar {field.titulo}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-4">
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                        {field.tipo_campo !== 'checkbox' && (
                            <Form.Label className="fs-5 mb-2">{field.titulo}</Form.Label>
                        )}
                        {renderFieldInput()}
                        {field.tipo_campo === 'multiselect' && (
                            <small className="text-muted mt-2 d-block">
                                Você pode selecionar múltiplas opções.
                            </small>
                        )}
                    </Form.Group>

                    <div className="d-flex justify-content-end mt-4 gap-2">
                        <button
                            type="button"
                            className="btn btn-light btn-lg"
                            onClick={onHide}
                            disabled={isUpdating}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary btn-lg"
                            disabled={isUpdating}
                        >
                            {isUpdating ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Salvando...
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </button>
                    </div>
                </Form>
            </Modal.Body>
            
            {/* Sem a tag style, vamos usar className diretamente no componente */}
        </Modal>
    );
};

export default EditFieldModal;